import Protected from "@/pages/protected";
import { DataService } from "@/services/data";
import { DraftView, PublishPreview } from "@/types/dialer/draft";
import { handle_dialer_error } from "@/utils/dialer-errors";
import { workflowEditorPath } from "@/utils/dialer-breadcrumbs";
import { handle_success } from "@/utils/wretch";
import { Outlet, redirect, RouteObject } from "react-router-dom";
import { loadBehaviorFields, loadLineage, loadNodeTypes, loadVariableFields } from "../tools/dialer/resolve";

/**
 * Opening the editor is a `POST`, because a draft is a row and it may not exist yet.
 * Idempotent by construction — there is one draft per workflow, so two tabs and two
 * people land on the same one — which is what lets a loader do it and the URL be
 * bookmarkable. `from_version` only decides where a *new* draft starts.
 *
 * The segment is the workflow's primary key. The API still accepts a code, so the
 * legacy bookmark route below can resolve either form before redirecting here.
 */
async function openDraft(workflowId: string, fromVersion: string | null): Promise<DraftView> {
    return (await DataService.client
        .url(`/dialer/workflows/${workflowId}/draft/`)
        .post(fromVersion ? { from_version: Number(fromVersion) } : {})
        .json()) as DraftView;
}

function draftEndpoint(url: URL, suffix = ""): string {
    return `/dialer/workflow-drafts/${url.searchParams.get("draft_id")}/${suffix}`;
}

/**
 * Preserve query string when rewriting a legacy bookmark onto the id-keyed editor.
 * `from_version` (and anything else a screen appends) must survive the hop.
 */
function withSearch(path: string, requestUrl: string): string {
    const search = new URL(requestUrl).search;
    return search ? `${path}${search}` : path;
}

/**
 * A fetcher POST revalidates every loader it previously read by default. Publication
 * consumes the draft, so reloading its preview at that point can only answer 404 and
 * replace the editor before its success effect navigates to the published version.
 */
function shouldRevalidateDraftResource(formMethod: string | undefined, defaultShouldRevalidate: boolean): boolean {
    return formMethod?.toLowerCase() === "post" ? false : defaultShouldRevalidate;
}

/**
 * The canvas editor, mounted outside the main layout.
 *
 * It is declared here rather than under `/dialer-workflows/:id` because the screen
 * needs the whole window: a graph is read by moving around it, and a page that
 * scrolls *around* a canvas that also scrolls gives two ways to move and no way to see
 * the drawing. `AppStandalone` is the shell this repository already uses for that — the
 * authentication guard and nothing else — so reusing it costs a route declaration
 * instead of an overlay fighting the sidebar for the window.
 *
 * The URL is keyed by workflow id, aligned with the SFLike detail
 * (`/dialer-workflows/:id/...`). Creation and the « Edit on canvas » action hand that
 * form out; the old `/outils/campagnes/workflows/:code/edition` address redirects here
 * so bookmarks and in-flight links keep working.
 *
 * The profile gate is repeated rather than inherited: leaving the main layout means
 * leaving its `Protected` wrapper, and an editor reachable by a profile the lineage
 * refuses would be a hole opened by a layout change.
 *
 * The guard is a route without a path of its own, as `standalone/dashboard.tsx` does, and
 * each child spells the whole URL. Two reasons, both load-bearing. A submission is routed
 * to the deepest match that *contributes a path segment*, so an `action` on an index child
 * under a fully-pathed parent is never reached and every autosave would answer « 405
 * Method Not Allowed ». And a parent that carried `workflows/:id` would itself match the
 * SFLike detail URL, adding a second candidate for a route that already exists.
 */
const DialerWorkflowEditorRoute: RouteObject = {
    element: (
        <Protected allowed={user => Boolean(user.can_open_dialer)}>
            <Outlet />
        </Protected>
    ),
    children: [
        {
            path: "dialer-workflows/:workflowId/edition",
            /**
             * Opening the draft is a write, so the default « revalidate after every
             * action » would re-post it on each autosave — and, after a discard, create a
             * brand new draft one millisecond after the one that was thrown away. Every
             * write already answers with the whole document, so the loader has nothing to
             * add while the screen is open.
             */
            shouldRevalidate: ({ currentParams, nextParams, formMethod, defaultShouldRevalidate }) => {
                if (currentParams.workflowId !== nextParams.workflowId) return true;
                // Case-folded on purpose: `v7_normalizeFormMethod` decides whether the
                // method arrives as `post` or `POST`, and reading only one of the two would
                // turn the flag into « re-open the draft on every autosave ».
                return shouldRevalidateDraftResource(formMethod, defaultShouldRevalidate);
            },
            loader: async ({ params, request }) => {
                const url = new URL(request.url);
                const [draft, nodeTypes, behaviorFields, variableFields] = await Promise.all([
                    openDraft(params.workflowId as string, url.searchParams.get("from_version")),
                    loadNodeTypes(),
                    loadBehaviorFields(),
                    loadVariableFields()
                ]);
                return { draft, nodeTypes, behaviorFields, variableFields };
            },
            /**
             * Three actions, where there used to be five. A node's configuration is no
             * longer submitted by a button of its own: it is a `patch_node` operation
             * carried by `save-draft`, like a drag, so the panel and the canvas write
             * through one queue. The dry run went with it — the save itself answers with
             * the recomputed outlets, which is a stronger statement than a preview.
             */
            action: async ({ request }) => {
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                try {
                    if (action === "save-draft") {
                        return handle_success(await DataService.client.url(draftEndpoint(url, "save/")).post(await request.json()).json());
                    }
                    if (action === "publish-draft") {
                        return handle_success(await DataService.client.url(draftEndpoint(url, "publish/")).post(await request.json()).json());
                    }
                    if (action === "discard-draft") {
                        await DataService.client.url(draftEndpoint(url)).delete().res();
                        return handle_success({ discarded: url.searchParams.get("draft_id") });
                    }
                    return handle_dialer_error(new Error(`Unknown draft action “${action}”.`));
                } catch (error) {
                    return handle_dialer_error(error);
                }
            },
            async lazy() {
                const { DialerWorkflowEditorPage } = await import("@/pages/dialer/workflows/editor");
                return { Component: DialerWorkflowEditorPage };
            }
        },
        {
            // Resource route: the publication dialog reads what publishing would produce.
            path: "dialer-workflows/:workflowId/edition/publication",
            shouldRevalidate: ({ formMethod, defaultShouldRevalidate }) =>
                shouldRevalidateDraftResource(formMethod, defaultShouldRevalidate),
            loader: async ({ request }) => {
                const url = new URL(request.url);
                return (await DataService.client.url(draftEndpoint(url, "publish-preview/")).get().json()) as PublishPreview;
            }
        },
        {
            // Legacy bookmarks keyed by business code → canonical id URL.
            path: "outils/campagnes/workflows/:code/edition",
            loader: async ({ params, request }) => {
                const lineage = await loadLineage(params.code as string);
                return redirect(withSearch(workflowEditorPath(lineage.workflow.id), request.url));
            }
        },
        {
            path: "outils/campagnes/workflows/:code/edition/publication",
            loader: async ({ params, request }) => {
                const lineage = await loadLineage(params.code as string);
                return redirect(withSearch(`${workflowEditorPath(lineage.workflow.id)}/publication`, request.url));
            }
        }
    ]
};

export default DialerWorkflowEditorRoute;
